import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Phone numbers, validated anywhere in the world and stored as E.164.
 *
 * This replaces a Kenya-only regex that lived on the profile page while the
 * application wizard accepted any international shape — so a supplier could
 * register with a foreign number and then be unable to edit their own contact
 * details. One rule now, used by both.
 *
 * `DEFAULT_COUNTRY` is what makes strict E.164 storage safe here. Most suppliers
 * type a bare local number, and 43 of the 58 numbers already in production are
 * in that form; parsing them against a default region turns them into E.164
 * rather than rejecting them. A number written with a leading `+` ignores the
 * default entirely, so nothing about this favours Kenyan numbers over any other
 * — it only decides how to read a number that carries no country code.
 */
const DEFAULT_COUNTRY = 'KE';

const parse = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  try {
    return parsePhoneNumberFromString(raw, DEFAULT_COUNTRY) || null;
  } catch {
    // parsePhoneNumberFromString returns undefined rather than throwing for the
    // inputs we expect, but a malformed value must never take a form down.
    return null;
  }
};

/** Whether this is a real, dialable number in some country. */
export const isValidPhone = (value) => {
  const parsed = parse(value);
  return Boolean(parsed && parsed.isValid());
};

/**
 * The number in E.164 (`+254712345678`), which is the shape to store.
 *
 * Returns null when the value cannot be parsed — callers must treat that as
 * "do not save", never as "save what the user typed". Storing an unnormalised
 * value would defeat the point of normalising at all.
 */
export const toE164 = (value) => {
  const parsed = parse(value);
  return parsed && parsed.isValid() ? parsed.number : null;
};

/**
 * The number spaced for reading. Falls back to the original string, because a
 * display helper must never blank out a value it merely failed to parse.
 */
export const formatPhone = (value) => {
  const parsed = parse(value);
  if (parsed && parsed.isValid()) return parsed.formatInternational();
  return String(value ?? '');
};

export const PHONE_HELPER =
  'Any country. A Kenyan number can be typed as 0712345678; others need their country code, e.g. +44 20 7946 0958';
